<template>
  <Header/>
  <div class="ins-root">
    <header class="ins-head">
      <h1>Инсайты</h1>
      <div class="subtitle">
        Матрица оттока «блок × слово» по данным utm_term. Клик по слову или ячейке раскрывает
        столбец в слои сессий; с выключенным тумблером «слои» — профиль слова и utm-фразы блока.
      </div>
    </header>

    <StatFilters
      :conversions="conversions"
      @apply="fetchData"
      @min-sessions="onMinSessions"
    />

    <div v-if="loading" class="dempty">Загружаем…</div>
    <div v-else-if="error" class="dempty">{{ error }}</div>
    <div v-else-if="!requested" class="dempty">
      Выберите проект, токен и период, затем нажмите «Запросить».
    </div>
    <div v-else-if="!blocks.length" class="dempty">
      По выбранным фильтрам данных нет.
    </div>
    <template v-else>
    <div class="panel">
      <!-- В слоях панели таблицы прячутся: сцена занимает место матрицы, и кнопки,
           которые к ней не относятся, только путали бы. Панель набора остаётся, как в
           прототипе (tableTools её не трогает): имя и поиск черновика переживают заход. -->
      <InsightsToolbar
        v-if="!layerOpen"
        :view="view" :metric="metric" :form="form" :showValues="showValues"
        :showSes="showSes" :showConv="showConv"
        :sort="sort" :blocks="blocks" :marker="marker" :dev="dev"
        :feed="feed" :delta="delta" :iqr="iqr" :brush="brush" :cmp="cmp"
        :bounds="bounds" :hint="hint"
        :segMode="segMode" :hasSegments="segments.length > 0" :segMarks="segMarks" :layers="layers"
        @update:view="setView" @update:metric="metric = $event"
        @update:form="form = $event" @update:showValues="showValues = $event"
        @update:showSes="showSes = $event" @update:showConv="showConv = $event"
        @update:sort="setSort" @update:marker="marker = $event"
        @update:dev="setDev" @update:feed="feed = $event"
        @update:delta="setDelta" @update:iqr="iqr = $event"
        @update:brush="setBrush" @update:cmp="setCmp"
        @update:segMode="setSegMode" @update:segMarks="segMarks = $event"
        @update:layers="layers = $event"
      />
      <SegmentBar
        v-if="segMode" v-show="view === 'matrix'"
        :segments="segments" :editId="segEdit" :parts="draftParts"
        :words="segWords" :countByWord="countByWord" :totalPhrases="totalPhrases" :nameMax="nameMax"
        @tab="segEdit = segEdit === $event ? null : $event"
        @new="segEdit = null; segDraft = []"
        @rename="onSegRename" @commit="onSegCommit" @done="segEdit = null"
        @parts="setDraftParts"
      />
      <template v-if="!layerOpen">
        <AbChips
          v-if="shownTests.length"
          :tests="shownTests"
          @open="openTestSlice" @edit="onAbEdit" @delete="onAbDelete" @report="openAbReport"
        />
        <InsightsFeed v-if="feed" :items="insights" @pick="onInsight"/>
      </template>

      <!-- Матрица остаётся в DOM и под сценой: сцена снимает с неё геометрию столбца
           (getColumnGeom), и при смене ширины окна её надо снимать заново. -->
      <div v-if="view === 'matrix'" class="ins-mwrap" :class="{ 'is-layers': !!layerOpen && !folding, 'is-folding': folding }">
        <WordMatrix
          ref="matrix"
          :blocks="blocks" :columns="words" :medians="medians" :bounds="bounds"
          :pick="pick" :raw="raw" :form="form" :showValues="showValues"
          :showSes="showSes" :showConv="showConv"
          :sel="sel" :sort="sort" :brushKeys="brushKeys" :sortMedian="sortMedian"
          :dev="dev" :delta="delta" :iqr="iqr" :marker="marker" :quarts="quarts"
          :cmp="cmp" :cmpKey="cmpKey" :hlKeys="hlKeys" :segMarks="segMarks" :nameMax="nameMax"
          :layers="layersOn"
          @select-word="onSelectWord"
          @select-cell="onSelectCell"
          @sort-block="cycleSort"
          @sort-total="cycleSort('total')"
          @ab="openAbForm($event, null)"
          @seg-pin="segPin" @seg-edit="segEditOpen" @seg-del="segDel"
        />
        <!-- Сцена стартует, когда таблица сложилась: иначе полёт шёл бы поверх гаснущих клеток. -->
        <div v-if="layerOpen && !folding" class="ins-layer-host">
          <!-- Пока сцены нет, крошки стоят над заглушкой: иначе выйти можно было бы только по Esc. -->
          <template v-if="sessionsState.error || !sessionsReady || !layerGeom || !layerColumn">
            <nav class="ins-crumbs" aria-label="путь">
              <button type="button" class="ins-cr" data-help="слои/крошки" @click="leaveLayers">Матрица</button>
              <i class="ins-cr-sep">›</i>
              <span class="ins-cr is-cur" aria-current="page">{{ layerColumn ? layerColumn.label : '' }}</span>
            </nav>
            <div class="dempty">{{ sessionsState.error || 'Загружаем сессии…' }}</div>
          </template>
          <LayerScene
            v-else
            ref="scene"
            :column="layerColumn" :blocks="blocks" :sessions="layerSessions"
            :metric="metric === 'cum' ? 'cum' : 'block'" :startBlock="layerOpen.startBlock"
            :geom="layerGeom" :allSessions="sessionsState.sessions"
            :ruleChanges="layerRuleChanges" :cellBounds="bounds" :cellMode="layerCellMode"
            :modalOpen="!!(abForm || abReport)" :story="storyBlock"
            @exit="exitLayers" @story-block="storyBlock = $event"
          >
            <!-- Крошки в строке LayerBar рядом с «фронт / вдоль стопки» (прототип 6620–6647).
                 Последний сегмент — текущее место и не кликается. Пока открыт блок, слово —
                 кнопка: клик возвращает сцену к слову. -->
            <template #crumbs>
              <nav class="ins-crumbs" aria-label="путь">
                <button type="button" class="ins-cr" data-help="слои/крошки" @click="leaveLayers">Матрица</button>
                <i class="ins-cr-sep">›</i>
                <span v-if="storyBlock === null" class="ins-cr is-cur" aria-current="page">{{ layerColumn.label }}</span>
                <template v-else>
                  <button type="button" class="ins-cr" data-help="слои/крошки" @click="toWordLevel">{{ layerColumn.label }}</button>
                  <i class="ins-cr-sep">›</i>
                  <span class="ins-cr is-cur" aria-current="page">Блок {{ storyBlock + 1 }}</span>
                </template>
              </nav>
            </template>
          </LayerScene>
        </div>
      </div>
      <WordStrips
        v-else
        :blocks="blocks" :words="stripWords" :medians="medians" :quarts="quarts"
        :pick="pick" :sel="stripsSel" :cmp="cmp" :cmpWi="stripsCmpWi"
        :iqr="iqr" :brush="brush" :brushSel="brushSel" :dev="dev"
        @select-cell="(wi, bi) => onSelectCell(keyOfWi(wi), bi)"
        @select-word="wi => onSelectWord(keyOfWi(wi))"
        @brush="brushSel = $event"
      />
      <DrillCard
        v-if="!layerOpen"
        :blocks="blocks" :columns="columns" :raw="raw" :pick="pick"
        :sel="sel" :cmp="cmp" :cmpKey="cmpKey" :iqr="iqr"
        :quarts="quarts" :medians="medians" :sortedObjects="sourceData"
        :convByPhrase="convByPhrase" :brushSel="brushSel"
        @clear="sel = null"
        @select-word="sel = { key: $event }"
        @select-cell="onSelectCell"
      />
    </div>

    <!-- Решётка: те же сессии в другой иерархии (текст → слово → фраза). Сессии тяжёлые,
         поэтому грузятся только при первом открытии решётки, слоёв или A/B.
         В слоях прячется: она про всю таблицу, а на экране разобран один столбец, и читалась
         бы как часть разбора. v-show, а не v-if — раскрытие и сборки переживают заход в слои. -->
    <section v-show="!layerOpen" class="panel">
      <div class="ins-grid-head">
        <div class="ins-grid-ttl">
          <h2>Решётка — воронка по блокам внутри уникального заголовка</h2>
          <div class="subtitle">Те же сессии, что в таблице выше, разложены по другой иерархии:
            текст → слово → словосочетание. Клетка — доля дошедших до блока. Срез задаёт сама
            таблица: пока в ней ничего не выбрано, решётка считает все слова; выбранный столбец
            она пересчитывает на него одного.</div>
        </div>
        <span class="tlabel">{{ headSlice.label }}</span>
        <button type="button" class="tgl" :aria-pressed="String(gridOpen)" @click="toggleGrid">
          {{ gridOpen ? 'свернуть' : 'показать' }}
        </button>
      </div>
      <template v-if="gridOpen">
        <div v-if="sessionsState.error" class="dempty">{{ sessionsState.error }}</div>
        <div v-else-if="!sessionsReady" class="dempty">Загружаем сессии…</div>
        <HeadGrid
          v-else
          :slice="headSlice" :sessions="sessionsState.sessions" :blocks="blocks" :phraseWords="phraseWords"
        />
      </template>
    </section>
    </template>

    <!-- Закрытие по mousedown, а не click: выделение текста в поле, отпущенное над фоном,
         иначе закрывало бы окно. -->
    <div v-if="abForm" class="ins-modal" @mousedown.self="abForm = null">
      <div class="ins-modal-win is-form" role="dialog" aria-modal="true">
        <div v-if="sessionsState.error" class="dempty">{{ sessionsState.error }}</div>
        <div v-else-if="!sessionsReady || !abColumn" class="dempty">Загружаем сессии…</div>
        <AbForm
          v-else
          :column="abColumn" :columns="columns" :sessions="sessionsState.sessions" :blocks="blocks"
          :balansirIndex="balansirIndex" :token="token" :test="abForm.test" :startBlock="abForm.startBlock"
          @save="onAbSave($event, false)" @save-open="onAbSave($event, true)" @cancel="abForm = null"
        />
      </div>
    </div>
    <div v-if="abReport" class="ins-modal" @mousedown.self="abReport = null">
      <div class="ins-modal-win is-report" role="dialog" aria-modal="true">
        <div v-if="sessionsState.error" class="dempty">{{ sessionsState.error }}</div>
        <div v-else-if="!sessionsReady" class="dempty">Загружаем сессии…</div>
        <AbReport
          v-else
          :tests="shownTests" :columns="columns" :sessions="sessionsState.sessions" :blocks="blocks"
          :tab="abReport.tab"
          @update="onAbUpdate" @close="abReport = null"
        />
      </div>
    </div>

    <HelpPop/>
  </div>
</template>

<script>
import axios from 'axios';
import { markRaw } from 'vue';
import Header from '@/components/Header.vue';
import StatFilters from '@/components/StatFilters.vue';
import InsightsToolbar from '@/components/insights/InsightsToolbar.vue';
import InsightsFeed from '@/components/insights/InsightsFeed.vue';
import WordMatrix from '@/components/insights/WordMatrix.vue';
import WordStrips from '@/components/insights/WordStrips.vue';
import DrillCard from '@/components/insights/DrillCard.vue';
import SegmentBar from '@/components/insights/SegmentBar.vue';
import HelpPop from '@/components/insights/HelpPop.vue';
import HeadGrid from '@/components/insights/HeadGrid.vue';
import LayerScene from '@/components/insights/layers/LayerScene.vue';
import { REDUCED } from '@/components/insights/layers/LayerCamera';
import AbChips from '@/components/insights/ab/AbChips.vue';
import AbForm from '@/components/insights/ab/AbForm.vue';
import AbReport from '@/components/insights/ab/AbReport.vue';
import { apiHost } from '@/utils/statFilters';
import {
  buildWordMatrix, buildSegmentColumn, filterByMinSessions, sortWords, median, quartiles,
  cumBuckets, adaptiveBuckets, computeInsights, conversionsByWord,
} from '@/utils/wordMatrix';
import {
  segAutoName, segNameMax, segShortName, togglePhrasesOfWord, loadSegments, saveSegments, newSegId,
} from '@/utils/segments';
import {
  unpackSessions, indexByPhrase, sessionsOfPhrases, resolveShown, loadSessions, clearSessionsCache,
  loadShownSources, ruleChangesFrom,
} from '@/utils/sessions';
import { getBalansirIndex } from '@/utils/balansirIndex';

// Мок — отдельным чанком и только в dev: в прод-сборке ветка с import() вырезается,
// и ни код, ни данные мока в бандл не попадают.
function loadMock() {
  if (process.env.NODE_ENV !== 'production') return import('@/utils/insightsMock');
  return Promise.reject(new Error('мок доступен только в dev-сборке'));
}

// Компоненты читают churn/reached как индексы блоков матрицы. /getSessions обязан отдавать
// тот же порядок (К1); если нет — сопоставляем по имени блока, а не по индексу.
function alignBlocks(sessions, from, to) {
  if (from.length === to.length && from.every((b, i) => b === to[i])) return;
  console.warn('Порядок блоков /getSessions не совпадает с матрицей, сопоставляю по имени:', from, to);
  const pos = new Map(to.map((b, i) => [b, i]));
  const map = from.map(b => (pos.has(b) ? pos.get(b) : -1));
  const at = i => (i >= 0 && i < map.length ? map[i] : -1);
  for (const s of sessions) {
    s.churn = at(s.churn);
    s.reached = at(s.reached);
  }
}

// ready отличает «загружено, но пусто» от «ещё не загружали».
// Тело 404 бэка на удаление уже удалённого теста (stat-back AbTestController.delete).
const AB_NOT_FOUND = 'Тест не найден у этого токена';
const emptySessions = () => ({ loading: false, ready: false, error: '', blocks: [], sessions: [], byPhrase: new Map(), shown: null });

export default {
  name: 'InsightsPage',
  components: {
    Header, StatFilters, InsightsToolbar, InsightsFeed, WordMatrix, WordStrips, DrillCard,
    SegmentBar, HelpPop, HeadGrid, LayerScene, AbChips, AbForm, AbReport,
  },
  data() {
    return {
      // sourceData, а не raw: метод raw(cell) уже занимает это имя,
      // а Vue не разрешает одноимённые data-поле и метод.
      sourceData: null,
      conversions: null,
      // Конверсии в разрезе utm_term: { фраза: [{t, form, type}] }.
      // Отдельным запросом, а не в /getstream: тяжёлый запрос матрицы
      // не должен падать целиком из-за разреза конверсий.
      convByPhrase: {},
      loading: false,
      // Успешный, но пустой ответ иначе неотличим от «ещё не запрашивали»,
      // и пользователь жмёт «Запросить» повторно вслепую.
      requested: false,
      error: '',
      matrix: { blocks: [], words: [], phrasesByWord: Object.create(null) },
      minSessions: '',
      reqId: 0,
      // Тело последнего запроса: по нему же лениво грузятся сессии (К1 — те же поля).
      token: '',
      period: null,

      view: 'matrix',      // 'matrix' | 'strips'
      metric: 'cum',       // 'cum' | 'plain'
      form: 'percent',     // 'percent' | 'value'
      showValues: true,
      // Что дописывать к слову в шапке матрицы. Подписи короткие («128s»,
      // «4c»): заголовки вертикальные, и каждое лишнее слово удлиняет шапку.
      showSes: true,
      showConv: false,
      sort: { key: 'ses', dir: 'desc' },
      // Сортировка, стоявшая до клика по блоку: третий клик возвращает её, а не сбрасывает.
      sortPrev: null,
      marker: false,
      dev: false,
      feed: false,
      delta: false,
      iqr: false,
      brush: false,
      cmp: false,

      sel: null,           // null | {key} | {key, bi}
      cmpKey: null,
      brushSel: null,      // null | {bi, lo, hi, wis: number[]} — wi слов, от WordStrips

      // Сегменты (К5). segEdit — id сегмента на правке, segDraft — состав нового.
      segments: [],
      segMode: false,
      segEdit: null,
      segDraft: [],
      segMarks: { lift: true, out: false },

      // Слои: клик по столбцу открывает сцену вместо DrillCard.
      layers: true,
      layerOpen: null,     // null | {key, startBlock}
      layerGeom: null,     // К6.geom столбца
      // Блок, открытый в сцене: сцена сообщает его событием story-block, страница
      // возвращает пропом story — крошка слова сбрасывает его в null.
      storyBlock: null,
      folding: false,      // таблица складывается перед стартом сцены

      sessionsState: emptySessions(),
      gridOpen: false,

      abTests: [],
      abForm: null,        // null | {key, test, startBlock}
      abReport: null,      // null | {tab}
      balansirIndex: null,
    };
  },
  computed: {
    // Мок только в dev-сборке: в проде ?mock=1 в адресе ничего не меняет.
    mock() {
      return process.env.NODE_ENV !== 'production' && !!this.$route && this.$route.query.mock === '1';
    },
    blocks() {
      return this.matrix.blocks;
    },
    convByWord() {
      return conversionsByWord(this.convByPhrase);
    },
    // buildSegmentColumn ждёт число конверсий на фразу, а не список событий.
    convCount() {
      const out = Object.create(null);
      for (const phrase in this.convByPhrase) {
        if (!Object.prototype.hasOwnProperty.call(this.convByPhrase, phrase)) continue;
        out[phrase] = (this.convByPhrase[phrase] || []).length;
      }
      return out;
    },
    phrasesByWord() {
      return this.matrix.phrasesByWord || Object.create(null);
    },
    // Столбцы-слова К4. Индекс wi проставляется один раз и указывает на позицию
    // в исходном массиве: на нём живёт распределение (WordStrips) и лента инсайтов.
    allWords() {
      const conv = this.convByWord;
      const pbw = this.phrasesByWord;
      return this.matrix.words.map((w, wi) => ({
        ...w,
        wi: wi,
        key: 'w:' + w.word,
        label: w.word,
        phrases: pbw[w.word] || [],
        conv: conv[w.word] ? conv[w.word].all : 0,
        seg: false,
      }));
    },
    countByWord() {
      const out = Object.create(null);
      for (const w of this.allWords) out[w.word] = w.phrases.length;
      return out;
    },
    totalPhrases() {
      const all = new Set();
      for (const w of this.allWords) for (const p of w.phrases) all.add(p);
      return all.size;
    },
    nameMax() {
      return segNameMax(this.allWords);
    },
    segWords() {
      return this.allWords.map(w => ({ word: w.word, phrases: w.phrases }));
    },
    // Столбцы-сегменты К4. Фразы, которых нет в периоде, дают нули — сегмент не пропадает.
    segColumns() {
      return this.segments.map(sg => ({
        ...buildSegmentColumn(this.sourceData, sg.parts, this.convCount),
        key: 'seg:' + sg.id,
        label: sg.name,
        word: sg.name,          // sortWords сортирует и сравнивает по word
        seg: true,
        pin: sg.pin !== false,
        id: sg.id,
        nParts: new Set(sg.parts.map(p => p.word + '\u0000' + p.phrase)).size,
        fullName: segAutoName(sg.parts, this.countByWord),
      }));
    },
    // Все столбцы К4 — для поиска по ключу, карточки и отчёта A/B.
    columns() {
      return this.allWords.concat(this.segColumns);
    },
    colByKey() {
      const m = new Map();
      for (const c of this.columns) m.set(c.key, c);
      return m;
    },
    // Тесты столбцов, которых нет на странице (сегмент из другого браузера, слово ушло из периода),
    // прячем, но не удаляем: столбец вернётся, и тест снова виден (R65).
    shownTests() {
      return this.abTests.filter(t => this.colByKey.has(t.columnKey));
    },
    // Показанные столбцы: слова после порога и откреплённые сегменты в общей сортировке,
    // закреплённые сегменты — справа, вне сортировки. Порог сегменты не фильтрует.
    words() {
      const visible = filterByMinSessions(this.allWords, this.minSessions);
      const free = this.segColumns.filter(c => !c.pin);
      return sortWords(visible.concat(free), this.sort.key, this.sort.dir, this.pick)
        .concat(this.segColumns.filter(c => c.pin));
    },
    // Распределение — только по словам, как медианы и квартили.
    stripWords() {
      return this.words.filter(c => !c.seg);
    },
    stripsSel() {
      const c = this.sel && this.colByKey.get(this.sel.key);
      if (!c || c.seg) return null;
      return this.sel.bi === undefined ? { wi: c.wi } : { wi: c.wi, bi: this.sel.bi };
    },
    stripsCmpWi() {
      const c = this.cmpKey && this.colByKey.get(this.cmpKey);
      return c && !c.seg ? c.wi : null;
    },
    brushKeys() {
      if (!this.brushSel) return null;
      return new Set(this.brushSel.wis.map(wi => this.allWords[wi]).filter(Boolean).map(w => w.key));
    },
    draftSeg() {
      return this.segEdit === null ? null : this.segments.find(s => s.id === this.segEdit) || null;
    },
    draftParts() {
      return this.draftSeg ? this.draftSeg.parts : this.segDraft;
    },
    // Слова, чьи фразы уже в наборе: их шапки жирные.
    hlKeys() {
      if (!this.segMode) return null;
      return new Set(this.draftParts.map(p => 'w:' + p.word));
    },
    layersOn() {
      return this.layers && this.view === 'matrix';
    },
    // Медианы и квартили считаются по полному набору слов: порог —
    // косметика отображения, он не должен смещать статистику. Сегменты сюда
    // не входят — это сборки из тех же слов.
    medians() {
      return this.blocks.map((_, bi) => median(this.allWords.map(w => this.pick(w.cells[bi]))));
    },
    quarts() {
      return this.blocks.map((_, bi) => quartiles(this.allWords.map(w => this.pick(w.cells[bi]))));
    },
    // Медиана по ключу сортировки — для маркера в матрице. Тоже по полному
    // набору слов: порог «мин. сессий» решает, какие столбцы показать, а не какой
    // у среза центр.
    sortMedian() {
      const key = this.sort.key;
      if (key === 'alpha') return 0;
      return median(this.allWords.map(w => {
        if (key === 'ses') return w.ses;
        if (key === 'conv') return w.conv || 0;
        if (key === 'total') return this.pick(w.cells[w.cells.length - 1]);
        return this.pick(w.cells[key]);
      }));
    },
    maxShare() {
      let max = 0;
      this.allWords.forEach(w => w.cells.forEach(c => { const v = this.pick(c); if (v > max) max = v; }));
      return max;
    },
    maxDelta() {
      let max = 0;
      this.allWords.forEach(w => w.cells.forEach((c, bi) => {
        const d = this.pick(c) - (bi > 0 ? this.pick(w.cells[bi - 1]) : 0);
        if (d > max) max = d;
      }));
      return max;
    },
    bounds() {
      // На приростах шкала уровня бесполезна: инкременты между блоками почти
      // всегда мельче первой же корзины, и матрица красится одним цветом.
      if (this.delta) return adaptiveBuckets(this.maxDelta);
      return this.layerBounds;
    },
    hint() {
      if (this.cmp && this.sel === null) return 'Сравнение: выберите первое слово в заголовке матрицы';
      if (this.cmp && this.cmpKey === null) return 'Сравнение: выберите второе слово';
      if (this.brush && this.view === 'strips') return 'Выделение: протяните мышью диапазон по любой оси';
      return '';
    },
    // Порог «мин. сессий со словом» решает только, о каких словах говорить:
    // слова, которых нет в матрице, не должны попадать и в ленту. Медианы и
    // квартили при этом приходят посчитанными по полному набору — цифры
    // порогом не двигаются.
    insights() {
      if (!this.feed) return [];
      const visible = filterByMinSessions(this.allWords, this.minSessions);
      const finalQuarts = this.quarts[this.blocks.length - 1] || [0, 0];
      const local = computeInsights(
        { blocks: this.blocks, words: visible }, this.pick, this.medians, finalQuarts);
      // computeInsights возвращает индекс внутри переданного массива —
      // переводим его в ключ столбца.
      return local.map(item => ({ ...item, key: visible[item.wi].key }));
    },

    sessionsReady() {
      return this.sessionsState.ready;
    },
    // фраза → [слово] для решётки: сессия относится ко всем словам своей фразы.
    phraseWords() {
      const out = Object.create(null);
      const pbw = this.phrasesByWord;
      for (const word in pbw) {
        for (const phrase of pbw[word]) (out[phrase] || (out[phrase] = [])).push(word);
      }
      return out;
    },
    // Срез решётки задаёт матрица: выбран столбец — его фразы; ничего не выбрано —
    // все слова после порога (null — порога нет, берётся вся таблица).
    headSlice() {
      const c = this.sel && this.colByKey.get(this.sel.key);
      // isColumn: срез — столбец матрицы, решётка ранжирует фразы внутри него, как сцена слоёв.
      if (c) return { label: c.seg ? 'сегмент «' + c.label + '»' : 'слово «' + c.label + '»', phrases: c.phrases, isColumn: true };
      if (!(Number(this.minSessions) > 0)) return { label: 'все слова', phrases: null };
      const set = new Set();
      for (const w of filterByMinSessions(this.allWords, this.minSessions)) for (const p of w.phrases) set.add(p);
      return { label: 'все слова от порога', phrases: [...set] };
    },
    layerColumn() {
      return this.layerOpen ? this.colByKey.get(this.layerOpen.key) || null : null;
    },
    // Даты смены заголовков у фраз столбца по снимкам балансировщика — событие «смена правил».
    layerRuleChanges() {
      const src = this.sessionsState.shown;
      if (!src || !this.layerColumn) return [];
      return ruleChangesFrom(src.snapshots, src.records, this.layerColumn.phrases, this.token);
    },
    // Шкала уровня для текущей метрики: ею красит матрица вне приростов.
    layerBounds() {
      return this.metric === 'cum' ? cumBuckets() : adaptiveBuckets(this.maxShare);
    },
    // Режим клеток матрицы для плиток среза в сцене: прирост Δ и отклонение от медианы.
    layerCellMode() {
      return { dev: this.dev, delta: this.delta, medians: this.medians };
    },
    layerSessions() {
      if (!this.layerColumn) return [];
      return sessionsOfPhrases(this.sessionsState.byPhrase, this.layerColumn.phrases);
    },
    abColumn() {
      return this.abForm ? this.colByKey.get(this.abForm.key) || null : null;
    },
  },
  watch: {
    // Правка сегмента из панели набора в слоях меняет сетку под сценой: новый столбец
    // сужает остальные, смена состава двигает сегмент в сортировке. Геометрию снимаем
    // заново, как renderAll10 при открытых слоях (прототип 7861–7869).
    words() {
      if (this.layerOpen) this.$nextTick(this.captureGeom);
    },
    // Сегменты сохраняются на каждое изменение списка — и состава, и имени, и булавки.
    segments: {
      deep: true,
      handler(list) {
        if (this.token) saveSegments(this.token, list);
      },
    },
  },
  created() {
    // Промис загрузки сессий — не реактивное поле: он нужен только, чтобы не грузить дважды.
    this.sessionsPromise = null;
    this.mockTests = [];
    this.foldTimer = null;
    this.foldEls = [];     // элементы матрицы, которым складывание поставило прозрачность
  },
  mounted() {
    window.addEventListener('resize', this.captureGeom);
    // На document, а не на window: сцена слушает window и видит уже помеченное событие.
    document.addEventListener('keydown', this.onKey);
    // Локальная Neo4j пустая — в dev с ?mock=1 страница открывается сразу на моке.
    if (this.mock) this.fetchData({ token: 'mock', utmGroupId: null, dateStart: null, dateEnd: null });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.captureGeom);
    document.removeEventListener('keydown', this.onKey);
    clearTimeout(this.foldTimer);
  },
  methods: {
    // pick — доля 0..1 выбранной метрики, raw — её абсолютное значение.
    // Цвет ячейки всегда считается по pick, чтобы переключение
    // «проценты ↔ значения» не перекрашивало матрицу.
    pick(cell) {
      if (!cell) return 0;
      return this.metric === 'cum' ? cell.chPc : cell.chP;
    },
    raw(cell) {
      if (!cell) return 0;
      return this.metric === 'cum' ? cell.chC : cell.ch;
    },
    keyOfWi(wi) {
      const w = this.allWords[wi];
      return w ? w.key : null;
    },
    onMinSessions(value) {
      this.minSessions = value;
      // Порог приходит на каждую набранную цифру. Сбрасываем выбор только
      // если выбранный столбец реально выпал из среза, иначе выделение
      // слетало бы прямо во время ввода.
      const visible = new Set(this.words.map(w => w.key));
      if (this.sel && !visible.has(this.sel.key)) this.sel = null;
      // Пара без первого слова бессмысленна: иначе подсказка звала бы выбрать
      // первое слово, а второй столбец продолжал бы гореть как выбранный
      // и приглушать всю остальную матрицу.
      if (this.cmpKey !== null && (!visible.has(this.cmpKey) || !this.sel)) this.cmpKey = null;
      this.brushSel = null;
      // Порог отрезал столбец, открытый в слоях: геометрию снять не с чего, закрываем слой.
      if (this.layerOpen && !visible.has(this.layerOpen.key)) this.exitLayers();
    },
    fetchData(params) {
      this.loading = true;
      this.error = '';
      // Конверсии прошлого среза сбрасываем сразу: два ответа приходят
      // независимо, и старые цифры не должны пережить новый фильтр.
      this.convByPhrase = {};
      // Ответы не отменяются, поэтому поздний ответ прошлого запроса мог бы
      // перерисовать данные поверх свежих. Считаем поколения и игнорируем чужие.
      const reqId = ++this.reqId;
      this.resetForRequest(params);
      if (this.mock) {
        loadMock()
          .then(m => { if (reqId === this.reqId) this.applyStream(m.mockStream()); })
          .catch(err => { if (reqId === this.reqId) { console.error(err); this.error = 'Мок не загрузился.'; } })
          .then(() => { if (reqId === this.reqId) this.loading = false; });
        return;
      }
      const body = {
        clientId: localStorage.getItem('clientId'),
        token: params.token,
        utmGroupId: params.utmGroupId,
        dateStart: params.dateStart,
        dateEnd: params.dateEnd,
      };
      // Разрез конверсий — не критичная часть: если эндпоинта нет (старый
      // бэкенд) или он упал, матрица должна открыться без него.
      axios.post(apiHost() + '/api/v1/getUtmConversions', body)
        .then(response => {
          if (reqId !== this.reqId) return;
          this.convByPhrase = response.data && typeof response.data === 'object' ? response.data : {};
        })
        .catch(err => {
          if (reqId !== this.reqId) return;
          console.error('Конверсии по utm_term недоступны:', err);
          this.convByPhrase = {};
        });
      axios.post(apiHost() + '/api/v1/getstream', body)
        .then(response => {
          if (reqId !== this.reqId) return;
          this.applyStream(response.data);
        })
        .catch(err => {
          if (reqId !== this.reqId) return;
          console.error('Ошибка при запросе данных:', err);
          this.error = 'Не удалось загрузить данные.';
        })
        .then(() => { if (reqId === this.reqId) this.loading = false; });
      this.loadAbTests(reqId);
    },
    // Всё, что привязано к прошлому токену и периоду: сессии, слои, окна A/B, сегменты.
    resetForRequest(params) {
      this.token = params.token || '';
      this.period = {
        token: params.token, utmGroupId: params.utmGroupId,
        dateStart: params.dateStart, dateEnd: params.dateEnd,
      };
      clearSessionsCache();
      this.sessionsPromise = null;
      this.sessionsState = emptySessions();
      this.unfoldMatrix();
      this.layerOpen = null;
      this.layerGeom = null;
      this.abForm = null;
      this.abReport = null;
      this.segMode = false;
      this.segEdit = null;
      this.segDraft = [];
      // Сегменты — по токену, из localStorage. Форму элементов проверяем: ключ мог
      // остаться от старой версии страницы.
      this.segments = loadSegments(this.token)
        .filter(s => s && typeof s.id === 'string' && Array.isArray(s.parts))
        .map(s => ({
          id: s.id, name: String(s.name || ''), auto: !!s.auto, pin: s.pin !== false,
          // битая часть уронила бы buildSegmentColumn на p.phrase
          parts: s.parts.filter(p => p && typeof p.word === 'string' && typeof p.phrase === 'string')
            .map(p => ({ word: p.word, phrase: p.phrase })),
        }));
      if (this.mock) this.abTests = this.mockTests;
    },
    applyStream(data) {
      this.sourceData = data.sortedObjects || {};
      this.conversions = data.statConversions || null;
      this.matrix = buildWordMatrix(this.sourceData);
      this.requested = true;
      this.sel = null;
      this.cmpKey = null;
      this.brushSel = null;
      this.sort = { key: 'ses', dir: 'desc' };
      this.sortPrev = null;
      // resetForRequest сбросил сессии, а решётка осталась открытой: без повторной
      // загрузки она так и висела бы на «Загружаем сессии…».
      if (this.gridOpen) this.ensureSessions();
    },
    // Новый ключ из списка забывает сортировку, к которой вёл бы третий клик по блоку (прототип 8065);
    // переключение направления ↓ её не трогает.
    setSort(value) {
      if (value.key !== this.sort.key) this.sortPrev = null;
      this.sort = value;
    },
    setView(value) {
      // Выделенный диапазон переживает переключение вида намеренно: в матрице
      // он гасит всё, кроме попавших в него слов, — ради этого его и тянут.
      // Тумблер «выделение» вид не трогает (прототип 8026-8037): погаси его — и
      // выделение снимется; в матрице протяжки нет, он там просто молчит.
      // Набор сегмента тоже переживает: панель в распределении просто спрятана,
      // черновик ждёт возврата в матрицу (прототип 8313).
      this.view = value;
    },
    // Esc закрывает сначала отчёт A/B, потом форму. preventDefault — чтобы сцена слоёв
    // не приняла тот же Esc за выход. Пока сцены нет (грузятся сессии, ошибка, складывание),
    // из слоёв выводит страница: сцене, которая обычно ловит Esc, ловить нечем.
    onKey(e) {
      if (e.key !== 'Escape') return;
      if (this.abReport) this.abReport = null;
      else if (this.abForm) this.abForm = null;
      else if (this.layerOpen && !this.$refs.scene) this.exitLayers();
      else return;
      e.preventDefault();
    },
    setDelta(value) {
      this.delta = value;
      if (!value) return;
      this.marker = false;   // на приростах маркер медианы бессмыслен
      this.dev = false;      // как и отклонение от медианы уровня
    },
    setDev(value) {
      this.dev = value;
      if (value) this.delta = false;
    },
    setBrush(value) {
      this.brush = value;
      if (!value) { this.brushSel = null; return; }
      // Выделение живёт в распределении — переключаем вид сразу.
      this.setView('strips');
    },
    setCmp(value) {
      this.cmp = value;
      this.cmpKey = null;
      // Выход из режима сбрасывает пару целиком: иначе первое слово остаётся
      // выбранным, а вся остальная матрица — приглушённой.
      if (!value) { this.sel = null; return; }
      if (this.sel && this.sel.bi !== undefined) this.sel = { key: this.sel.key };
    },
    // Клик по шапке. Порядок перехвата как в прототипе (3030–3046): набор сегмента →
    // второе слово сравнения → слои → выбор. Первое слово пары при слоях открывает слои
    // (Ruling 55).
    onSelectWord(key) {
      const col = this.colByKey.get(key);
      if (!col) return;
      // Набор перехватывает клики только в матрице: в распределении его панель спрятана.
      if (this.segMode && this.view === 'matrix') {
        // клик по сегменту открывает его состав на правку — без похода за карандашом
        if (col.seg) { this.segEdit = col.id; return; }
        this.setDraftParts(togglePhrasesOfWord(this.draftParts, col.label, col.phrases));
        return;
      }
      const whole = this.sel && this.sel.bi === undefined;
      if (this.cmp && whole && this.sel.key !== key) {
        this.cmpKey = this.cmpKey === key ? null : key;
        return;
      }
      if (this.layersOn) {
        // повторный клик по уже выбранному столбцу снимает выбор, как в прототипе (3040)
        if (whole && this.sel.key === key) { this.sel = null; this.cmpKey = null; return; }
        this.sel = { key: key };
        this.cmpKey = null;
        this.openLayers(key, null);
        return;
      }
      this.sel = (whole && this.sel.key === key) ? null : { key: key };
      this.cmpKey = null;
    },
    onSelectCell(key, bi) {
      // Клетка проваливает в слои и в режиме набора, и в сравнении (Ruling 48): панель
      // набора остаётся над сценой, сравнение выход из слоёв снимает вместе с выбором.
      // Клетка сегмента в слои не ведёт, только выбирается, как в прототипе (3134, !e.seg).
      const col = this.colByKey.get(key);
      const same = this.sel && this.sel.key === key && this.sel.bi === bi;
      if (this.layersOn && col && !col.seg) {
        // та же клетка повторно снимает выбор (прототип 3138)
        if (same) { this.sel = null; return; }
        this.sel = { key: key, bi: bi };
        this.openLayers(key, bi);
        return;
      }
      this.sel = same ? null : { key: key, bi: bi };
    },
    onInsight(item) {
      // В режиме сравнения выбором может быть только слово: `sel` с указанным
      // блоком сломал бы признак «первое слово пары», и следующий клик по
      // заголовку начинал бы пару заново вместо её завершения.
      if (this.cmp) { this.sel = { key: item.key }; this.cmpKey = null; return; }
      this.sel = item.bi === null ? { key: item.key } : { key: item.key, bi: item.bi };
    },
    // Три клика по блоку или итогу: ↓, ↑, третий — вернуть сортировку, стоявшую ДО
    // клика по блоку (прототип 2865–2880). Иначе «по итогу» из списка молча пропадала.
    cycleSort(key) {
      if (this.sort.key !== key) {
        // запоминаем только не-блочный выбор: перескок с блока на блок ничего не затирает
        if (typeof this.sort.key !== 'number') this.sortPrev = { ...this.sort };
        this.sort = { key: key, dir: 'desc' };
      } else if (this.sort.dir === 'desc') {
        this.sort = { key: key, dir: 'asc' };
      } else if (this.marker) {
        this.sort = { key: key, dir: 'desc' };   // маркеру нужен ключ, снимать нечего
      } else {
        this.sort = this.sortPrev || { key: 'ses', dir: 'desc' };
        this.sortPrev = null;
      }
    },

    // ---- сегменты ----
    setSegMode(value) {
      this.segMode = value;
      if (!value) { this.segEdit = null; this.segDraft = []; }
    },
    segName(parts) {
      return segShortName(parts, this.nameMax, this.countByWord, this.totalPhrases)
        || segAutoName(parts, this.countByWord);
    },
    setDraftParts(parts) {
      const seg = this.draftSeg;
      if (!seg) { this.segDraft = parts; return; }
      seg.parts = parts;
      // имя, которое не писали руками, едет за составом
      if (seg.auto) seg.name = this.segName(parts);
    },
    onSegRename(name) {
      const seg = this.draftSeg;
      if (!seg) return;
      // пустое поле — снова автоимя; своё имя больше не перетирается
      seg.auto = !name;
      seg.name = name || this.segName(seg.parts);
    },
    onSegCommit(name) {
      const parts = this.segDraft;
      if (!parts.length) return;
      this.segments.push({
        id: newSegId(), name: name || this.segName(parts), auto: !name, pin: true, parts: parts,
      });
      // Столбец добавлен — набор кончился. Включённый режим продолжал бы перехватывать
      // клики по шапке и мешал бы просто смотреть таблицу.
      this.setSegMode(false);
    },
    segPin(id) {
      const seg = this.segments.find(s => s.id === id);
      if (seg) seg.pin = !seg.pin;
    },
    segEditOpen(id) {
      this.segMode = true;
      this.segEdit = id;
    },
    segDel(id) {
      const key = 'seg:' + id;
      this.segments = this.segments.filter(s => s.id !== id);
      this.segEdit = null; // × всегда закрывает правку, как в прототипе (3078)
      if (this.sel && this.sel.key === key) { this.sel = null; this.cmpKey = null; }
      if (this.cmpKey === key) this.cmpKey = null;
      // Столбца не стало — его тесты A/B выбрасываются, как в прототипе (11421-11423): считать
      // по ним нечего. Сегмент удаляется сразу; тест на бэке, который не удалился, только в лог.
      const dead = this.abTests.filter(t => t.columnKey === key);
      if (!dead.length) return;
      this.abTests = this.abTests.filter(t => t.columnKey !== key);
      if (this.mock) { this.mockTests = this.abTests; return; }
      dead.forEach(t => axios.post(apiHost() + '/api/v1/abtest/delete', { id: t.id, token: this.token })
        .catch(err => console.error('Тест удалённого сегмента не удалён:', err)));
    },

    // ---- сессии ----
    // Лениво: при первом открытии слоёв, решётки или A/B. Заголовки старых сессий
    // восстанавливаются по балансировщику; если он недоступен — сессии без них.
    ensureSessions() {
      if (this.sessionsPromise) return this.sessionsPromise;
      const reqId = this.reqId;
      const st = this.sessionsState;
      st.loading = true;
      st.error = '';
      const token = this.token;
      const load = this.mock
        ? loadMock().then(m => unpackSessions(m.mockSessions()))
        : loadSessions(this.period).then(res => loadShownSources()
          .then(src => {
            resolveShown(res.sessions, token, src.records, src.snapshots, Date.now());
            // снимки и записи нужны ещё сцене слоёв — для события «смена правил»
            if (reqId === this.reqId) st.shown = markRaw(src);
            return res;
          })
          .catch(err => { console.error('Показанные заголовки не восстановлены:', err); return res; }));
      this.sessionsPromise = load
        .then(res => {
          if (reqId !== this.reqId) return;
          alignBlocks(res.sessions, res.blocks, this.blocks);
          // markRaw: тысячи сессий в реактивной обёртке — лишние прокси на каждое чтение.
          st.sessions = markRaw(res.sessions);
          st.byPhrase = markRaw(indexByPhrase(res.sessions));
          st.blocks = this.blocks;
          st.loading = false;
          st.ready = true;
        })
        .catch(err => {
          if (reqId !== this.reqId) return;
          console.error('Сессии не загрузились:', err);
          st.loading = false;
          st.error = 'Не удалось загрузить сессии.';
          this.sessionsPromise = null;
        });
      return this.sessionsPromise;
    },
    toggleGrid() {
      this.gridOpen = !this.gridOpen;
      if (this.gridOpen) this.ensureSessions();
    },

    // ---- слои ----
    openLayers(key, startBlock) {
      // Окна A/B настраиваются по таблице, а в слоях её нет.
      this.abForm = null;
      this.abReport = null;
      this.layerOpen = { key: key, startBlock: startBlock };
      this.layerGeom = null;
      this.storyBlock = null;
      this.ensureSessions();
      // Геометрию снимаем после перерисовки: панели над матрицей в слоях спрятаны,
      // и матрица успевает сдвинуться вверх.
      this.$nextTick(() => {
        this.captureGeom();
        this.foldMatrix(key);
      });
    },
    // Таблица складывается за 0,4 с: гаснет всё, кроме раскрываемого столбца, и только
    // потом стартует сцена (прототип 7335–7361).
    foldMatrix(key) {
      const m = this.$refs.matrix;
      if (!this.layerOpen || !m || REDUCED) return;
      const j = String(this.words.findIndex(c => c.key === key));
      const els = Array.from(m.$el.querySelectorAll('.wm-hcell, .ins-segh-wrap, .wm-c2, .ins-agg, .ins-sep, '
        + '.ins-seg-halo, .wm-vmed, .wm-vmed-pill, .wm-vmed-zone, .wm-cell'));
      // Прозрачность и переход ставим в style: :class у клеток перетёр бы свой класс.
      for (const x of els) {
        x.style.transition = 'opacity .40s cubic-bezier(.4, 0, .2, 1)';
        x.style.opacity = x.classList.contains('wm-cell') && x.dataset.idx === j ? '1' : '0';
      }
      this.foldEls = els;
      this.folding = true;
      this.foldTimer = setTimeout(() => { this.folding = false; }, 420);
    },
    unfoldMatrix() {
      clearTimeout(this.foldTimer);
      const touched = this.foldEls.length > 0;
      for (const x of this.foldEls) { x.style.transition = ''; x.style.opacity = ''; }
      this.foldEls = [];
      this.folding = false;
      const el = this.$refs.matrix && this.$refs.matrix.$el;
      if (!touched || !el) return;
      // таблица проявляется целиком, как в прототипе на выходе
      el.style.opacity = '0';
      requestAnimationFrame(() => requestAnimationFrame(() => { el.style.opacity = ''; }));
    },
    // Крошка «Матрица»: выход с той же анимацией, что по Esc. Сцены ещё нет (грузятся
    // сессии) — выходим сразу.
    leaveLayers() {
      if (this.$refs.scene) this.$refs.scene.exit();
      else this.exitLayers();
    },
    // Крошка слова: проп story → null, сцена снимает блок, выбор и перекадрируется
    // (прототип 6636–6641) и событием story-block возвращает null обратно.
    toWordLevel() {
      this.storyBlock = null;
    },
    captureGeom() {
      if (!this.layerOpen || !this.$refs.matrix) return;
      const g = this.$refs.matrix.getColumnGeom(this.layerOpen.key);
      // та же геометрия — сцену не перестраиваем
      if (JSON.stringify(g) !== JSON.stringify(this.layerGeom)) this.layerGeom = g;
    },
    exitLayers() {
      this.unfoldMatrix();
      this.layerOpen = null;
      this.layerGeom = null;
      this.storyBlock = null;
      // выход снимает и выбор: иначе карточка и срез решётки остались бы на раскрытом столбце
      this.sel = null;
      this.cmpKey = null;
    },

    // ---- A/B ----
    loadAbTests(reqId) {
      if (this.mock) return;
      this.abTests = [];
      // Тесты держим от старого к новому, как в прототипе: бэкенд отдаёт новые первыми,
      // а отчёт по умолчанию берёт последний, то есть только что заведённый.
      axios.post(apiHost() + '/api/v1/abtest/list', { token: this.token })
        .then(r => { if (reqId === this.reqId) this.abTests = ((r.data && r.data.tests) || []).reverse(); })
        .catch(err => console.error('Список A/B тестов недоступен:', err));
    },
    ensureBalansir() {
      if (this.balansirIndex) return;
      if (this.mock) { this.balansirIndex = markRaw(new Map()); return; }
      getBalansirIndex()
        .then(index => { this.balansirIndex = markRaw(index); })
        .catch(err => console.error('Балансировщик недоступен:', err));
    },
    openAbForm(key, test) {
      if (!this.colByKey.has(key)) return;
      const bi = this.sel && this.sel.key === key && this.sel.bi !== undefined ? this.sel.bi : null;
      this.abForm = { key: key, test: test, startBlock: test ? test.block : bi };
      this.ensureSessions();
      this.ensureBalansir();
    },
    onAbEdit(test) {
      this.openAbForm(test.columnKey, test);
    },
    openAbReport(tab) {
      this.abReport = { tab: tab };
      this.ensureSessions();
    },
    // Открыть срез теста — выставить в матрице тот выбор, из которого тест заводили.
    openTestSlice(test) {
      if (!this.colByKey.has(test.columnKey)) return;
      const byBlock = (test.metric === 'reach' || test.metric === 'medBlock') && test.block !== null && test.block !== undefined;
      this.sel = byBlock ? { key: test.columnKey, bi: test.block } : { key: test.columnKey };
    },
    // В ?mock=1 тесты живут в памяти страницы: бэкенда с токеном «mock» нет.
    saveTest(test) {
      const body = { ...test, token: this.token };
      const done = saved => {
        const i = this.abTests.findIndex(t => t.id === saved.id);
        if (i >= 0) this.abTests.splice(i, 1, saved); else this.abTests.push(saved);
        return saved;
      };
      if (this.mock) return Promise.resolve(done({ ...body, id: body.id || 'ab' + Date.now().toString(36) }));
      return axios.post(apiHost() + '/api/v1/abtest/save', { test: body })
        .then(r => done(r.data.test));
    },
    onAbSave(test, open) {
      this.saveTest(test)
        .then(saved => {
          this.abForm = null;
          if (open) this.openTestSlice(saved);
        })
        .catch(err => { console.error('Тест не сохранён:', err); alert('Не удалось сохранить тест.'); });
    },
    onAbUpdate(test) {
      this.saveTest(test)
        .catch(err => { console.error('Тест не сохранён:', err); alert('Не удалось сохранить тест.'); });
    },
    onAbDelete(id) {
      const drop = () => { this.abTests = this.abTests.filter(t => t.id !== id); if (this.mock) this.mockTests = this.abTests; };
      if (this.mock) { drop(); return; }
      axios.post(apiHost() + '/api/v1/abtest/delete', { id: id, token: this.token })
        .then(drop)
        .catch(err => {
          // 404 от нашего бэка — теста уже нет (удалили в другой вкладке): чип просто убираем.
          // Чужой 404 (прокси, неверный адрес API) — ошибка, тест на сервере мог остаться.
          const r = err.response;
          if (r && r.status === 404 && r.data && r.data.message === AB_NOT_FOUND) { drop(); return; }
          console.error('Тест не удалён:', err); alert('Не удалось удалить тест.');
        });
    },
  },
};
</script>

<style scoped>
.ins-head { margin-bottom: 18px; }
/* Сцена слоёв ложится поверх матрицы в тех же координатах: геометрия К6 снята
   от левого верхнего угла матрицы, а он совпадает с углом .ins-mwrap. */
.ins-mwrap { position: relative; transition: min-height .5s cubic-bezier(.22,.75,.2,1); }
.ins-mwrap.is-layers > .wm { visibility: hidden; }
.ins-layer-host { position: absolute; left: 0; top: 0; right: 0; min-height: 100%; }
.ins-grid-head { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.ins-grid-ttl { margin-right: auto; }
/* Отбивка описания от тулбара решётки, как .panel .desc в прототипе (178): 16 здесь + 18 у .ins-gr. */
.ins-grid-ttl .subtitle { margin-bottom: 16px; font-size: 13px; max-width: none; }
/* Складывание таблицы при входе в слои: гаснет всё, кроме раскрываемого столбца
   (прозрачность выставляет foldMatrix), клики на это время не принимаются. */
.ins-mwrap > .wm { transition: opacity .38s ease; }
.ins-mwrap.is-folding > .wm { pointer-events: none; }
</style>
